import type { AppState, Signal } from "../data/model";

const project = (projectId: string) => `/projects/${projectId}`;

export interface AddPreset {
    env?: string;
    kind?: "service" | "infra";
    /** An infrastructure template (component id) */
    template?: string;
    /** A Microtica starter template, by its key in the template list */
    starter?: string;
    /** A service already in the project, to run in another environment */
    service?: string;
}

const withEnv = (path: string, envId?: string) => (envId ? `${path}?env=${encodeURIComponent(envId)}` : path);

export const routes = {
    overview: (projectId: string) => `${project(projectId)}/overview`,
    /** The assistant on its own page; a conversation by id, or the latest when none is given */
    assistant: (projectId: string, chatId?: string) => `${project(projectId)}/assistant${chatId ? `/${chatId}` : ""}`,
    services: (projectId: string, envId?: string) => withEnv(`${project(projectId)}/services`, envId),
    service: (projectId: string, name: string, envId?: string) => withEnv(`${project(projectId)}/services/${encodeURIComponent(name)}`, envId),
    signals: (projectId: string, open?: string) => `${project(projectId)}/signals${open ? `?open=${encodeURIComponent(open)}` : ""}`,
    /** With `create`, opens the new environment dialog; `then` is where to go with the new environment */
    environments: (projectId: string, create?: { then?: string }) => {
        if (!create) return `${project(projectId)}/environments`;
        const query = new URLSearchParams({ new: "1", ...(create.then ? { then: create.then } : {}) });
        return `${project(projectId)}/environments?${query}`;
    },
    templates: (projectId: string) => `${project(projectId)}/templates`,
    /** Every change from push to deploy; filters (env, service, status) live in the query */
    activity: (projectId: string, filters: { env?: string; service?: string; status?: string } = {}) => {
        const query = new URLSearchParams(Object.entries(filters).filter(([, v]) => !!v) as [string, string][]).toString();
        return `${project(projectId)}/activity${query ? `?${query}` : ""}`;
    },
    deployments: (projectId: string, envId?: string) => withEnv(`${project(projectId)}/deployments`, envId),
    deploy: (projectId: string, envId: string, deploymentId: string, kind: "app" | "infra") =>
        `${project(projectId)}/deployments/${envId}/${deploymentId}${kind === "infra" ? "?kind=infra" : ""}`,
    /** Add a service or infrastructure; whatever's known up front skips its question */
    add: (projectId: string, known: AddPreset = {}) => {
        const query = new URLSearchParams(Object.entries(known).filter(([, v]) => !!v) as [string, string][]).toString();
        return `${project(projectId)}/add${query ? `?${query}` : ""}`;
    },
    component: (projectId: string, envId: string, name: string) => `${project(projectId)}/environments/${envId}/components/${encodeURIComponent(name)}/overview`,
    pipelines: (projectId: string) => `${project(projectId)}/pipelines`,
    pipeline: (projectId: string, pipelineId: string) => `${project(projectId)}/pipelines/${encodeURIComponent(pipelineId)}`,
    run: (projectId: string, pipelineId: string, runId: string, step?: string) =>
        `${project(projectId)}/pipelines/${encodeURIComponent(pipelineId)}/runs/${encodeURIComponent(runId)}${step ? `?step=${encodeURIComponent(step)}` : ""}`,
    /** The cost explorer, with its filters as the page keeps them in the URL */
    costs: (projectId: string, params: Record<string, string> = {}) => {
        const query = new URLSearchParams(params).toString();
        return `${project(projectId)}/cost-explorer${query ? `?${query}` : ""}`;
    },
    /** Cost schedules, opened on one when given (its environment id or resource group) */
    schedules: (projectId: string, scheduleId?: string) =>
        `${project(projectId)}/cost-explorer?${new URLSearchParams({ view: "optimizer", ...(scheduleId ? { schedule: scheduleId } : {}) })}`,
    envSettings: (projectId: string, envId: string) => `${project(projectId)}/environments/${envId}/settings/general`,
};

/** Cluster resources are named "<envId>-<component>"; the classic routes use the component part */
export function componentIdOf(app: AppState) {
    return app.clusterId.startsWith(`${app.env.id}-`) ? app.clusterId.slice(app.env.id.length + 1) : app.clusterId;
}

export function classicAppUrl(projectId: string, app: AppState, section: string) {
    return `${project(projectId)}/environments/${app.env.id}/components/${componentIdOf(app)}/apps/${app.namespace}/${app.name}/${section}`;
}

/** Where a signal's one action leads */
export function signalAction(projectId: string, signal: Signal): { to: string; label: string } {
    switch (signal.kind) {
        case "env-deploy-failed":
            return { to: routes.deployments(projectId, signal.env.id), label: `Open ${signal.env.name} deploys` };
        case "component-failed":
            return { to: routes.component(projectId, signal.env.id, signal.subject), label: `Open ${signal.subject}` };
        case "cluster-unreachable":
        case "no-pods":
            return { to: routes.services(projectId, signal.env.id), label: `Open ${signal.env.name}` };
        default:
            return { to: routes.service(projectId, signal.subject, signal.env.id), label: `Open ${signal.subject}` };
    }
}
